import React, { useEffect, useState } from 'react';

import { useGetOne } from 'react-admin';

import {
  Card,
  CardContent,
  Box,
  CardHeader,
  LinearProgress,
  Typography,
  Divider,
} from '@mui/material';

import ReactApexChart from 'react-apexcharts';
import { isEmpty } from 'lodash';
import SelectInputButton from '../../layout/inputFields/SelectInputButton';
import theme from '../../../theme';
import useHoverSeriesUpdateLabel from '../../../hooks/useHoverSeriesUpdateLabel';

const useGetGraphData = (
  data: {
    numberPingByStatus: {
      pending?: number;
      confirmed?: number;
      resolved?: number;
      ignored?: number;
    };
  },
  chartRef: React.RefObject<ReactApexChart>,
) => {
  const [hasData, setHasData] = useState(false);
  const [options, setOptions] = useState<ReactApexChart['props']['options']>();
  const [series, setSeries] = useState<ReactApexChart['props']['series']>();

  const {
    getTotalOptions,
    attachLegendHoverEvents,
  } = useHoverSeriesUpdateLabel({ chartRef });

  useEffect(() => {
    if (!data) return;

    const colors = ['#1F487E', theme.palette.success.main, '#F3B61F', '#B8DBD9'];

    const labels = ['Confirmed', 'Resolved', 'Pending', 'Ignored'];

    const newSeries = [
      data.numberPingByStatus.confirmed ?? 0,
      data.numberPingByStatus.resolved ?? 0,
      data.numberPingByStatus.pending ?? 0,
      data.numberPingByStatus.ignored ?? 0,
    ] satisfies ReactApexChart['props']['series'];

    const totalOptions = getTotalOptions({
      series: newSeries,
      colors,
      labels,
      totalColor: theme.palette.primary.main,
      totalLabel: 'Total pings',
    });

    const newOptions = {
      colors,
      labels,
      chart: {
        redrawOnParentResize: true,
        height: 400,
        zoom: {
          enabled: false,
        },
        toolbar: {
          show: false,
        },
        events: {
          mounted: attachLegendHoverEvents,
          updated: attachLegendHoverEvents,
        },
      },
      legend: {
        position: 'bottom',
        show: !isEmpty(data.numberPingByStatus),
      },
      plotOptions: {
        pie: {
          donut: {
            labels: {
              show: true,
              total: {
                show: true,
                ...totalOptions,
              },
            },
          },
        },
      },
    } satisfies ReactApexChart['props']['options'];

    setHasData(true);
    setOptions(newOptions);
    setSeries(newSeries);
  }, [attachLegendHoverEvents, data, getTotalOptions]);

  return {
    hasData,
    options,
    series,
  };
};

const PingAnalytics = () => {
  const [period, setPeriod] = useState('30');
  const { data, isLoading } = useGetOne('dashboard', { id: `pings/${period}` });

  const chartRef = React.useRef<ReactApexChart>(null);
  const { hasData, options, series } = useGetGraphData(data, chartRef);

  return (
    <Card
      variant="outlined"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'stretch',
        height: '100%',
      }}
    >
      <CardHeader
        title="Pings"
        action={(
          <SelectInputButton
            choices={[
              { id: '1', name: 'Last day' },
              { id: '7', name: 'Last week' },
              { id: '30', name: 'Last month' },
              { id: '90', name: 'Last quarter' },
              { id: '365', name: 'Last year' },
              { id: '99999', name: 'All time' },
            ]}
            value={period}
            setValue={setPeriod}
          />
        )}
      />
      <Divider />
      <CardContent
        sx={{
          flexGrow: 1,
        }}
      >
        {isLoading && <LinearProgress /> }
        {hasData && <ReactApexChart ref={chartRef} options={options} series={series} type="donut" height={300} />}
        {(!isLoading && !hasData) && (
          <Box margin={10} width="100%">
            <Typography align="center"> No pings available </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default PingAnalytics;
